import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import {
  selectToken,
  setCredentials,
  useAppDispatch,
  useAppSelector,
} from '@lotta/self-service/lottery-fe/data-access-state';
import { Home } from '@lotta/self-service/lottery-fe/feature-home';
import {
  LotteryTemplates,
  CreateLottery,
} from '@lotta/self-service/lottery-fe/feature-create-lottery';
import { DrawDetails } from '@lotta/self-service/lottery-fe/feature-draw-details';
// import { Statistics } from '@lotta/self-service/lottery-fe/feature-statistics';
import { UserGuide } from '@lotta/self-service/lottery-fe/feature-user-guide';
import { LotteryDetails } from '@lotta/self-service/lottery-fe/feature-lottery-details';
import { Layout } from '@lotta/self-service/lottery-fe/feature-layout';
import Loading, { LoadingBall } from '@ingka/loading';
import Text from '@ingka/text';
import { EditLottery } from '@lotta/self-service/lottery-fe/feature-edit-lottery';
import { NoMatch } from '@lotta/self-service/lottery-fe/ui-common';
import { useTranslation } from 'react-i18next';
import { AccessVerification } from '@lotta/self-service/lottery-fe/feature-access-verification';
import { LocaleVerification } from '@lotta/self-service/lottery-fe/feature-locale-verification';
import { RoleVerification } from '@lotta/self-service/lottery-fe/feature-role-verification';
import { CopyLottery } from '@lotta/self-service/lottery-fe/feature-copy-lottery';
import { FeatureFlags } from '../feature-flags/FeatureFlags';
import { Config } from '../feature-flags/Config';

type AppProps = {
  redirectCounter: {
    count: number;
    reset: () => void;
  };
};

export const App: React.FC<AppProps> = ({ redirectCounter }) => {
  const { t } = useTranslation();
  const token = useAppSelector(selectToken);
  const { getAccessTokenSilently, loginWithRedirect, isAuthenticated, user, isLoading } =
    useAuth0();
  const dispatch = useAppDispatch();

  const redirectLoopError = redirectCounter.count >= 3;

  useEffect(() => {
    if (user) {
      const setToken = async () => {
        const token = await getAccessTokenSilently();

        if (token) {
          dispatch(setCredentials({ token, name: user.given_name as string }));
        }
      };
      setToken();
    }
  }, [isAuthenticated, dispatch, getAccessTokenSilently, user]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !redirectLoopError) {
      loginWithRedirect({
        appState: {
          returnTo: window.location.pathname,
          count: redirectCounter.count + 1,
        },
      });
    }
  }, [isAuthenticated, isLoading, loginWithRedirect, redirectCounter.count, redirectLoopError]);

  // Clear the redirect counter if we haven't been redirected after some time.
  useEffect(() => {
    const timerId = setTimeout(redirectCounter.reset, 10000);
    return () => clearTimeout(timerId);
  }, [redirectCounter]);

  if (redirectLoopError) {
    return <>{t('general-enable-cookies')}</>;
  }

  if (isLoading) {
    return (
      <Loading text={`${t('app-loading')}`}>
        <LoadingBall />
      </Loading>
    );
  }

  if (!isAuthenticated) {
    return (
      <Loading text={`${t('app-loading-authenticating')}`}>
        <LoadingBall />
      </Loading>
    );
  }

  if (!token) {
    return (
      <Loading text={`${t('app-loading')}`}>
        <LoadingBall />
      </Loading>
    );
  }

  return (
    <Routes>
      <Route element={<Config />}>
        <Route path="/:countryCode/:language" element={<Layout />}>
          <Route element={<AccessVerification />}>
            <Route element={<LocaleVerification />}>
              <Route element={<FeatureFlags />}>
                <Route index element={<Home />} />
                <Route
                  element={<RoleVerification allowedRoles={['SUPER_ADMIN', 'ADMIN', 'EDITOR']} />}
                >
                  <Route path="create" element={<LotteryTemplates />} />
                  <Route path="create/:templateId/*" element={<CreateLottery />} />
                </Route>
                <Route element={<RoleVerification allowedRoles={['SUPER_ADMIN', 'ADMIN']} />}>
                  <Route path="lottery/:lotteryId/edit/*" element={<EditLottery />} />
                  <Route path="lottery/:lotteryId/copy/*" element={<CopyLottery />} />
                </Route>
                <Route path="lottery/:lotteryId" element={<LotteryDetails />} />
                <Route path="lottery/:lotteryId/draw/:lotteryDrawId" element={<DrawDetails />} />
                {/* <Route path="statistics" element={<Statistics />} /> */}
                <Route path="user-guide" element={<UserGuide />} />
                <Route
                  path="claim-my-prize-preview"
                  element={
                    <div>
                      <Text headingSize="m">{t('claim-prize-preview-page')}</Text>
                    </div>
                  }
                />
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<NoMatch />} />
      </Route>
    </Routes>
  );
};

export default App;
